import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { Container, Row, Col, Pagination, Button, Badge, Card, Spinner, Alert, Modal } from 'react-bootstrap';
import '../../CSS/DeviceDetails.css'; // Create this CSS file for custom styling

const customIcon = new L.Icon({
    iconUrl: 'marker-icon.png',
    iconRetinaUrl: 'marker-icon-2x.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowUrl: 'marker-shadow.png',
    shadowSize: [41, 41]
});

export const route = "/it/device/:deviceId";
export const role = "IT";

function DeviceDetails() {
    const { deviceId } = useParams();
    const [device, setDevice] = useState(null);
    const [feedback, setFeedback] = useState('');
    const [issues, setIssues] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [imageUrl, setImageUrl] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [, setErrorMessage] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        const fetchDeviceDetails = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/devices/${deviceId}/`);
                const data = await response.json();
                if (response.ok) {
                    setDevice(data);
                    setImageUrl(data.imageUrl);
                } else {
                    console.error('Failed to fetch device details');
                }
            } catch (error) {
                console.error('Error fetching device details:', error);
            }
        };

        const fetchIssues = async (page) => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/devices/${deviceId}/issues?page=${page}`);

                const data = await response.json();
                setIssues(data.issues);
                setTotalPages(data.totalPages);
            } catch (error) {
                console.error('Error fetching device details:', error)
            }
        };

        fetchIssues(currentPage)
        fetchDeviceDetails();
    }, [deviceId, currentPage]);

    const handleImageUpload = async (event) => {
        const file = event.target.files[0];
        const formData = new FormData();
        formData.append('image', file);

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/devices/img/${deviceId}`, {
                method: 'POST',
                body: formData,
                mode: 'no-cors' // Add this line to enable CORS
            });

            if (response.status === 413) {
                setErrorMessage('File size too large. Please upload an image under 5MB.'); // Custom error message for 413
            } else if (response.ok) {
                const data = await response.json();
                setImageUrl(data.imageUrl);
                setErrorMessage(''); // Clear any previous errors
            } else {
                setErrorMessage('Failed to upload image. Please try again.');
            }
        } catch (error) {
            console.error('Error uploading image:', error);
        }
    };

    const handleImageDelete = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/devices/img/${deviceId}`, {
                method: 'DELETE',
            });
            if (response.ok) {
                setImageUrl(null);
            } else {
                console.error('Failed to delete image');
            }
        } catch (error) {
            console.error('Error deleting image:', error);
        }
    };

    const handleModalToggle = () => {
        setShowModal(!showModal);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleDelete = async () => {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/devices/${deviceId}/`, {
            method: 'DELETE'
        });
        if (response.ok) {
            navigate('/it/device/list'); // redirect to the device list after deletion
        } else {
            alert('Failed to delete the device');
        }
    };

    const handlePing = async () => {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/devices/ping/${deviceId}/`);
        const { isOnline } = await response.json();
        if (device.isOnline !== isOnline) {
            setDevice({ ...device, isOnline });
            setFeedback('Device status updated!')
        } else {
            setFeedback('Device status checked, no change!')
        }
    };

    const handleReboot = async (ip) => {
        await fetch(`${process.env.REACT_APP_API_URL}/reboot/${ip}/`, {
            method: 'GET'
        });
    };

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return new Date(dateString).toLocaleDateString(undefined, options);
    };

    if (!device) return <Spinner animation="border" variant="primary" className="d-block mx-auto my-5" />;

    return (
        <Container className="my-5">
            <Card className="mb-4 shadow-sm">
                <Card.Header className="text-white">
                    <h4>Device Details</h4>
                </Card.Header>
                <Card.Body>
                    <h5 className="card-title">{device.friendlyName}</h5>
                    <p className="card-text"><strong>Type:</strong> {device.type}</p>
                    <p className="card-text"><strong>Name:</strong> {device.name}</p>
                    <p className="card-text"><strong>Location:</strong>
                        <MapContainer center={[device.location.lat, device.location.lng]} zoom={18} style={{ height: '400px', width: '100%' }}>
                            <TileLayer
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                            />
                            <Marker position={[device.location.lat, device.location.lng]} icon={customIcon}>
                                <Popup>{device.friendlyName}</Popup>
                            </Marker>
                        </MapContainer>
                    </p>
                    <p className="card-text"><strong>Pier:</strong> {device.pier}</p>
                    {device.type === 'Agilysys POS' && (
                        <>
                            <p className="card-text"><strong>Terminal ID:</strong> {device.terminalId}</p>
                            <p className="card-text"><strong>Profile ID:</strong> {device.profileId}</p>
                        </>
                    )}
                    <p className="card-text"><strong>IP:</strong> {device.ip}</p>
                    <p className="card-text"><strong>Status:</strong>
                        <Badge className={`ms-2 ${device.isOnline ? 'bg-success' : 'bg-danger'}`}>
                            {device.isOnline ? 'Online' : 'Offline'}
                        </Badge>
                    </p>
                    <Button variant="primary" onClick={() => navigate(`/it/device/edit/${device._id}`)} className="me-2">Edit</Button>
                    <Button variant="danger" onClick={handleDelete} className="me-2">Delete</Button>
                    <Button variant="info" onClick={handlePing} className="me-2">Check Status</Button>
                    {device.type === 'Ticket Reader' && (
                        <>
                            <Button variant="warning" onClick={handleReboot(device.ip)} className="me-2">Reboot</Button>
                            <Link to={`/it/device/configure/${device._id}`} className="btn btn-primary m-2">
                                Configure Ticket Reader
                            </Link>
                        </>
                    )}
                    {feedback && <Alert variant="info" className="mt-2">{feedback}</Alert>}
                </Card.Body>
            </Card>
            <Row className="mt-4">
                <Col>
                    <Card>
                        <Card.Header as="h2">Device Image</Card.Header>
                        <Card.Body>
                            {imageUrl ? (
                                <>
                                    <img
                                        src={`${process.env.REACT_APP_API_URL}${imageUrl}`}
                                        alt="Device"
                                        onClick={handleModalToggle}
                                        style={{ cursor: 'pointer', maxWidth: '100%', maxHeight: '400px', display: 'block', margin: '0 auto' }}
                                    />
                                    <Modal show={showModal} onHide={handleModalToggle}>
                                        <Modal.Header closeButton>
                                            <Modal.Title>Device Image</Modal.Title>
                                        </Modal.Header>
                                        <Modal.Body>
                                            <img src={`${process.env.REACT_APP_API_URL}${imageUrl}`} alt="Device" style={{ width: '100%' }} />
                                        </Modal.Body>
                                        <Modal.Footer>
                                            <Button variant="secondary" onClick={handleModalToggle}>
                                                Close
                                            </Button>
                                            <Button variant="danger" onClick={handleImageDelete}>
                                                Delete Image
                                            </Button>
                                            <Button>
                                                <input type="file" onChange={handleImageUpload} />
                                            </Button>
                                        </Modal.Footer>
                                    </Modal>
                                </>
                            ) : (
                                <Button>
                                    <input type="file" onChange={handleImageUpload} />
                                </Button>
                            )}
                        </Card.Body>
                    </Card>
                    <Card>
                        <Card.Header as="h2">Reported Issues</Card.Header>
                        <Card.Body>
                            <ul className="list-group list-group-flush">
                                {issues.map((issue) => (
                                    <li key={issue._id} className="list-group-item">
                                        {issue.description} - {new Date(issue.createdAt).toLocaleString()}
                                    </li>
                                ))}
                            </ul>
                            <Pagination className="mt-3">
                                {Array.from({ length: totalPages }, (_, i) => (
                                    <Pagination.Item
                                        key={i + 1}
                                        active={i + 1 === currentPage}
                                        onClick={() => handlePageChange(i + 1)}
                                    >
                                        {i + 1}
                                    </Pagination.Item>
                                ))}
                            </Pagination>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
            <Card className="mt-4 shadow-sm">
                <Card.Header className="bg-secondary text-white">
                    <h4>Status Log</h4>
                </Card.Header>
                <Card.Body>
                    <div className="timeline">
                        {device.statusLogs.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)).map((log, index) => (
                            <div className="timeline-item" key={index} style={{ flexDirection: "column" }}>
                                {index === 0 || formatDate(device.statusLogs[index - 1].timestamp) !== formatDate(log.timestamp) ? (
                                    <div className="timeline-date">
                                        {formatDate(log.timestamp)}
                                    </div>
                                ) : null}
                                <div className="timeline-content" >
                                    <div className="timeline-icon">
                                        {log.status === 'Online' ? (
                                            <svg className="text-success" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                                <path d="M16 4.146l-8.036 8.036L0 4.146 1.564 2.58l6.4 6.4L14.4 2.58 16 4.146zM1.564 8.82L0 7.256l1.564-1.564 1.564 1.564-1.564 1.564zm3.2-3.2L4.8 4.056 6.364 5.62 4.8 7.184 3.764 6.148zM8.036 5.62L9.6 4.056l1.564 1.564-1.564 1.564-1.564-1.564zm3.2-3.2L12.8 0 14.364 1.564 12.8 3.128l-1.564-1.564zm3.2-3.2L16 0l-1.564 1.564L12.8 3.128 14.364 1.564zm-1.564 4.8l1.564 1.564-1.564 1.564-1.564-1.564 1.564-1.564z" />
                                            </svg>
                                        ) : (
                                            <svg className="text-danger" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                                <path d="M16 4.146l-8.036 8.036L0 4.146 1.564 2.58l6.4 6.4L14.4 2.58 16 4.146zM1.564 8.82L0 7.256l1.564-1.564 1.564 1.564-1.564 1.564zm3.2-3.2L4.8 4.056 6.364 5.62 4.8 7.184 3.764 6.148zM8.036 5.62L9.6 4.056l1.564 1.564-1.564 1.564-1.564-1.564zm3.2-3.2L12.8 0 14.364 1.564 12.8 3.128l-1.564-1.564zm3.2-3.2L16 0l-1.564 1.564L12.8 3.128 14.364 1.564zm-1.</svg>564 4.8l1.564 1.564-1.564 1.564-1.564-1.564 1.564-1.564z" />
                                            </svg>
                                        )}
                                    </div>
                                    <div className="timeline-details">
                                        <span className="timeline-time">{new Date(log.timestamp).toLocaleTimeString()}</span>
                                        <span className={`badge ${log.status === 'Online' ? 'bg-success' : 'bg-danger'}`}>{log.status}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </Card.Body>
            </Card>
        </Container>
    );
}

export default DeviceDetails;